import { Business, Close, ModeEdit, Person } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import api from "../../../../services/api";
import { useNotification } from "../../../../componentes/notificationProvider";
import { useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LoadingButton } from "@mui/lab";
import ImageButton from "../../../../componentes/imageButton";
import {
  maskCnpj,
  maskCPF,
  maskPhone,
  removeMask,
} from "../../../../utils/maskFields";

import { validarEmail } from "../../../../utils/validateFields";

import { validarCpf } from "../../../../utils/validateCpf";
import { validarCnpj } from "../../../../utils/validateCnpj";
import useStyles from "./styles";
import { RowsData } from "../../../../componentes/administracao/types";
import { AxiosError } from "axios";

interface ModalEditProfileProps {
  openEditProfileModal: boolean;
  setOpenEditProfileModal: (value: boolean) => void;
  selectedRow: RowsData;
  rows: RowsData[];
  setRows: React.Dispatch<React.SetStateAction<RowsData[]>>;
}

function BoxPreview(params: {
  foto: string;
  setFoto: () => void;
  pessoaFisica: boolean;
}) {
  const { foto, setFoto = () => {}, pessoaFisica } = params;
  const theme = useTheme();
  const styles = useStyles(theme);
  return (
    <Paper sx={styles.paperPreview} variant="outlined">
      <Box sx={styles.boxCover}></Box>

      <ImageButton
        startIcon={<ModeEdit />}
        component="label"
        size="small"
        setFoto={setFoto}
        sx={styles.button}
        isButton={true}
      >
        Editar Foto
      </ImageButton>

      <Box sx={styles.boxAvatar}>
        <Avatar src={foto} color="secondary" sx={styles.avatar} />{" "}
        <Tooltip title={pessoaFisica ? "pessoa física" : "pessoa jurídica"}>
          <Box component="label" sx={styles.boxIcon}>
            {pessoaFisica ? <Person /> : <Business />}
          </Box>
        </Tooltip>
      </Box>
    </Paper>
  );
}
const ModalEditProfile: React.FC<ModalEditProfileProps> = (params) => {
  const theme = useTheme();
  const notify = useNotification();
  const {
    openEditProfileModal,
    setOpenEditProfileModal,
    selectedRow,
    rows = [] as RowsData[],
    setRows = () => {},
  } = params;
  const [id, setId] = useState("");

  const [email, setEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);

  const [nome, setNome] = useState<string>("");
  const [sobrenome, setSobrenome] = useState<string>("");
  const [nomeFantasia, setNomeFantasia] = useState<string>("");
  const [dataDeNascimento, setDataDeNascimento] = useState<Dayjs | null>(null);
  const [telefone, setTelefone] = useState<string>("");
  const [cpf, setCpf] = useState<string>("");
  const [cnpj, setCnpj] = useState<string>("");
  const [foto, setFoto] = useState<string>("");
  const [pessoaFisica, setPessoaFisica] = useState<boolean>(false);
  const [hasProfile, setHasProfile] = useState<boolean>(false);
  const styles = useStyles(theme);

  useEffect(() => {
    setIsSubmit(false);
    setId(String(selectedRow?.id));
    setEmail(String(selectedRow?.email));
    if (selectedRow?.PerfilCliente) {
      setNome(String(selectedRow?.PerfilCliente?.nome));
      setSobrenome(String(selectedRow?.PerfilCliente?.sobrenome));
      setNomeFantasia(String(selectedRow?.PerfilCliente?.nomeFantasia));
      setDataDeNascimento(dayjs(selectedRow?.PerfilCliente?.dataDeNascimento));
      setTelefone(String(selectedRow?.PerfilCliente?.telefone));
      setCpf(String(selectedRow?.PerfilCliente?.cpf));
      setCnpj(String(selectedRow?.PerfilCliente?.cnpj));
      setFoto(String(selectedRow?.PerfilCliente?.foto));
      setPessoaFisica(Boolean(selectedRow?.PerfilCliente?.pessoaFisica));
      setHasProfile(true);
      return;
    }
    setHasProfile(false);
  }, [selectedRow]);

  async function handleApiUpdateProfile(data: Partial<RowsData>) {
    try {
      const response = await api.put("/admin/updateUser", {
        ...data,
        logsLoginClienteId:
          JSON.parse(String(localStorage.getItem("logs")))?.id || "",
      });

      if (response.status === 200) {
        setRows((rows) =>
          rows.map((row) => {
            return row.id === id ? { ...row, ...response.data } : row;
          })
        );
      }
      notify("Perfil atualizado com sucesso!", "success");
    } catch (error) {
      console.error(error);
      if (error instanceof AxiosError && error?.response?.data && error.status)
        notify(error?.status || 500, undefined, "error", error);
      else notify(null, "Erro ao editar perfil do usuário!", "error");
    } finally {
      setLoading(false);
      setOpenEditProfileModal(false);
    }
  }

  const handleClose = () => {
    setOpenEditProfileModal(false);
    setIsSubmit(false);
  };

  const handleSubmit = () => {
    setIsSubmit(true);
    let data = {};
    if (!email || !validarEmail(email)) return;
    if (!hasProfile)
      data = {
        id,
        email,
      };
    else if (pessoaFisica) {
      if (
        !nome ||
        !sobrenome ||
        !cpf ||
        !validarCpf(cpf) ||
        !dataDeNascimento ||
        !telefone
      )
        return;
      else
        data = {
          id,
          nome,
          sobrenome,
          dataDeNascimento,
          cpf: removeMask(cpf),
          email,
          telefone: removeMask(telefone),
          foto,
        };
    } else {
      if (!nomeFantasia || !cnpj || !validarCnpj(cnpj) || !telefone) return;
      else
        data = {
          id,
          nomeFantasia,
          cnpj: removeMask(cnpj),
          email,
          telefone: removeMask(telefone),
          foto,
        };
    }

    handleApiUpdateProfile(data);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  const handleValidateEmail = () => {
    const verificaEmail = rows?.some(
      (row: RowsData) => email === row?.email && row.id !== id
    );
    if (verificaEmail) {
      return true;
    }
    return false;
  };

  return (
    <Modal open={openEditProfileModal} onClose={handleClose} sx={styles.modal}>
      <Paper sx={styles.paper}>
        <DialogTitle color="primary" sx={styles.dialogTitle}>
          <Box sx={styles.boxHeader}>
            <Box sx={styles.boxTitle}>
              <IconButton size="small">
                <Person sx={{ color: "primary.main" }} />
              </IconButton>
              <Typography fontWeight={500}>Editar Detalhes</Typography>
            </Box>

            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenEditProfileModal(false);
              }}
              //sx={styles.iconButton}
            >
              <Close />
            </IconButton>
          </Box>

          <Divider />

          {hasProfile ? (
            <Box sx={{ ...styles.boxRowFields, mt: 1 }}>
              <Typography sx={styles.subtitle}>Prévia</Typography>
              <BoxPreview
                foto={foto}
                setFoto={() => setFoto}
                pessoaFisica={pessoaFisica}
              />
            </Box>
          ) : (
            <Alert sx={styles.alert} severity="info">
              Usuário não possui dados de perfil cadastrados
            </Alert>
          )}
        </DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          <Grid container columnSpacing={1.5} rowSpacing={2}>
            {pessoaFisica && hasProfile && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={styles.boxRowFields}>
                  <TextField
                    label="Nome"
                    variant="filled"
                    size="small"
                    sx={styles.textfield}
                    autoComplete="off"
                    disabled={loading}
                    value={nome}
                    onChange={(e) => {
                      setNome(e.target.value);
                    }}
                    error={isSubmit && !nome}
                    helperText={isSubmit && !nome && "Campo inválido"}
                  />
                </Box>
              </Grid>
            )}

            {pessoaFisica && hasProfile && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={styles.boxRowFields}>
                  <TextField
                    label="Sobrenome"
                    variant="filled"
                    size="small"
                    sx={styles.textfield}
                    autoComplete="off"
                    disabled={loading}
                    value={sobrenome}
                    onChange={(e) => {
                      setSobrenome(e.target.value);
                    }}
                    error={isSubmit && !sobrenome}
                    helperText={isSubmit && !sobrenome && "Campo inválido"}
                  />
                </Box>
              </Grid>
            )}

            {!pessoaFisica && hasProfile && (
              <Grid size={{ xs: 12, sm: 12 }}>
                <Box sx={styles.boxRowFields}>
                  <TextField
                    label="Nome fantasia"
                    variant="filled"
                    size="small"
                    sx={styles.textfield}
                    autoComplete="off"
                    disabled={loading}
                    value={nomeFantasia}
                    onChange={(e) => {
                      setNomeFantasia(e.target.value);
                    }}
                    error={isSubmit && !nomeFantasia}
                    helperText={isSubmit && !nomeFantasia && "Campo inválido"}
                  />
                </Box>
              </Grid>
            )}

            {pessoaFisica && hasProfile && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={styles.boxRowFields}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={"pt-br"}
                  >
                    <DatePicker
                      label="Data de nascimento"
                      sx={styles.textfield}
                      maxDate={dayjs()}
                      openTo="year"
                      disabled={loading}
                      value={dataDeNascimento}
                      onChange={(newValue: Dayjs | null) => {
                        setDataDeNascimento(newValue);
                      }}
                      slotProps={{
                        textField: {
                          variant: "filled",
                          error: isSubmit && !dataDeNascimento,
                          helperText:
                            isSubmit && !dataDeNascimento
                              ? "Campo obrigatório"
                              : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>
            )}

            {hasProfile && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={styles.boxRowFields}>
                  <TextField
                    label="Telefone"
                    variant="filled"
                    size="small"
                    sx={styles.textfield}
                    autoComplete="off"
                    disabled={loading}
                    value={maskPhone(telefone)}
                    onChange={(e) => {
                      setTelefone(e.target.value);
                    }}
                    error={isSubmit && !telefone}
                    helperText={isSubmit && !telefone && "Campo inválido"}
                  />
                </Box>
              </Grid>
            )}

            {pessoaFisica && hasProfile && (
              <Grid size={{ xs: 12, sm: 12 }}>
                <Box sx={styles.boxRowFields}>
                  <TextField
                    label="CPF"
                    variant="filled"
                    size="small"
                    sx={styles.textfield}
                    autoComplete="off"
                    disabled={loading}
                    value={maskCPF(cpf)}
                    onChange={(e) => {
                      setCpf(e.target.value);
                    }}
                    error={isSubmit && (!cpf || !validarCpf(cpf))}
                    helperText={
                      isSubmit && (!cpf || !validarCpf(cpf)) && "CPF inválido"
                    }
                  />
                </Box>
              </Grid>
            )}

            {!pessoaFisica && hasProfile && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={styles.boxRowFields}>
                  <TextField
                    label="CNPJ"
                    variant="filled"
                    size="small"
                    sx={styles.textfield}
                    autoComplete="off"
                    disabled={loading}
                    value={maskCnpj(cnpj)}
                    onChange={(e) => {
                      setCnpj(e.target.value);
                    }}
                    error={isSubmit && (!cnpj || !validarCnpj(cnpj))}
                    helperText={
                      isSubmit &&
                      (!cnpj || !validarCnpj(cnpj)) &&
                      "CNPJ inválido"
                    }
                  />
                </Box>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 12 }}>
              <Box sx={styles.boxRowFields}>
                <TextField
                  //width={"100%"}
                  label="E-mail"
                  variant="filled"
                  onKeyDown={handleKeyDown}
                  size="small"
                  sx={styles.textfield}
                  autoComplete="off"
                  disabled={loading}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  error={
                    (isSubmit && (!email || !validarEmail(email))) ||
                    handleValidateEmail()
                  }
                  helperText={
                    (isSubmit &&
                      (!email || !validarEmail(email)) &&
                      "Campo inválido") ||
                    (handleValidateEmail() && "E-mail já cadastrado")
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button
            disabled={loading}
            onClick={() => {
              handleClose();
            }}
            sx={{ color: "primary.main" }}
          >
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={handleSubmit}
          >
            Salvar
          </LoadingButton>
        </DialogActions>
      </Paper>
    </Modal>
  );
};

export default ModalEditProfile;
